<script setup>
import BreadCrumbs from '@/components/breadcrumbs/BreadCrumbs.vue'
import ContentSubheaderPage from '@/components/content/ContentSubheaderPage.vue'
import ConferenceComment from '@/components/conferences/ConferenceComment.vue'
import { onMounted, ref } from 'vue'
import { useConferencesStore } from '@/store/conferences'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useUserStore } from '@/store/user'
import NewsSlider from '@/components/news/NewsSlider.vue'
import NewsFiles from '@/components/news/NewsFiles.vue'
import NewsVideos from '@/components/news/NewsVideos.vue'

const router = useRouter()
const conferencesStore = useConferencesStore()
const authStore = useAuthStore()
const userStore = useUserStore()

const conferenceId = ref(0)
const conferenceData = ref({})
const files = ref([])

const commentText = ref('')
const comments = ref([])

conferencesStore.$subscribe((_, state) => {
    comments.value = state.conferenceComments
})

const addComment = async () => {
    if (commentText.value.trim().length === 0) return
    if (authStore.email.length === 0) {
        alert('Войдите в аккаунт чтобы оставлять комментарии')
        return
    }
    if (!userStore.changeUser.allow_comments) {
        console.log(userStore.changeUser.allow_comments)
        alert('Недостаточно прав для оставления комментариев')
        return
    }

    await conferencesStore.addComment(
        authStore.id,
        conferenceId.value,
        commentText.value
    )

    if (authStore.email.length > 0) {
        await conferencesStore.getComments(conferenceId.value, authStore.id)
        comments.value = conferencesStore.conferenceComments
    } else {
        await conferencesStore.getComments(conferenceId.value)
        comments.value = conferencesStore.conferenceComments
    }

    commentText.value = ''
}

onMounted(async () => {
    conferenceId.value = Number(router.currentRoute.value.params.id)

    await conferencesStore.getSingleConference(conferenceId.value)
    conferenceData.value = conferencesStore.singleConference
    console.log(conferenceData.value)

    if (conferenceData.value.files !== null) {
        conferenceData.value.files
            .map((file) => file.split('||'))
            .forEach((file) => {
                let arr = file[0].split('/')
                files.value.push({
                    name: arr[arr.length - 1],
                    href: window.document.location.origin + '/' + file[0]
                })
            })
    }

    if (
        conferenceData.value.video_links !== null &&
        conferenceData.value.video_links.length > 0
    ) {
        conferenceData.value.video_links = conferenceData.value.video_links.map(
            (link) => {
                if (link.includes('https://vk.com/video')) {
                    const idOidStr = link
                        .substr(link.indexOf('video'))
                        .substring(5, link.indexOf('_'))
                    const idOidArray = idOidStr.split('_')
                    return `https://vk.com/video_ext.php?oid=${idOidArray[0]}&id=${idOidArray[1]}&hd=2`
                } else if (link.includes('https://rutube.ru/video')) {
                    return link.replace('video', 'play/embed')
                }

                return link
            }
        )
    }

    await conferencesStore.incrementConferenceViews(conferenceId.value)
    await userStore.getUser(authStore.id)

    if (authStore.email.length > 0) {
        await conferencesStore.getComments(conferenceId.value, authStore.id)
        comments.value = conferencesStore.conferenceComments
    } else {
        await conferencesStore.getComments(conferenceId.value)
        comments.value = conferencesStore.conferenceComments
    }
})
</script>

<template>
    <div class="container">
        <bread-crumbs />
        <content-subheader-page
            class="conference-page-content-subheader"
            :text="conferenceData.text_header"
        />
        <div class="conference-date">
            <img
                src="/svg/calendar_date.svg"
                alt="Календарь"
            />
            {{
                new Date(conferenceData.creation_time).toLocaleDateString(
                    'ru-RU',
                    { day: 'numeric', month: 'long', year: 'numeric' }
                )
            }}
        </div>
        <div class="conference-main-content" v-html="conferenceData.text_main">
        </div>
        <news-slider
            v-if="conferenceData.images !== null"
            class="news-page-slider"
            :sliderImg="
                conferenceData.images ? conferenceData.images.map((img) => '/' + img) : []
            "
        />
        <NewsVideos
            v-if="conferenceData.video_links !== null"
            class="news-page-videos"
            :news-videos="conferenceData.video_links"
        />
        <news-files
            v-if="conferenceData.files !== null"
            class="news-page-files"
            :files="files"
        />
        <div class="conference-comments-section">
            <h2 class="conference-comments-title">Комментарии</h2>
            <div class="conference-comments-input">
                <v-avatar
                    icon="mdi-account"
                    color="#EFF0F2"
                    size="60"
                />
                <v-text-field
                    v-model="commentText"
                    variant="outlined"
                    placeholder="Ваш комментарий"
                    hide-details
                    @keyup.enter="addComment"
                />
            </div>
            <div class="conference-comments-list">
                <conference-comment
                    v-for="comment in comments"
                    :key="comment.id"
                    v-bind="comment"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.container {
    margin-top: 32px;
    margin-left: 104px;
    margin-right: 104px;
    text-align: left;

    @media screen and (max-width: 767px) {
        margin-left: 40px;
        margin-right: 40px;
        margin-top: 24px;
    }
}

.conference-page-content-subheader {
    text-align: left;
}

.conference-date {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #a2a2a2;
    font-size: 20px;
    line-height: 28px;
    margin-top: 20px;
}

.conference-main-content {
    margin-top: 25px;
    font-size: 20px;
    line-height: 28px;

    :deep(ul) {
        list-style-position: inside;
    }
    
    :deep(ol) {
        list-style-position: inside;
    }
}

.conference-comments-section {
    margin-top: 30px;
}

.conference-comments-title {
    font-size: 28px;
    font-weight: 500;
    line-height: 36px;
    color: #0635ac;
}

.conference-comments-input {
    display: flex;
    align-items: center;
    gap: 25px;
    margin-top: 40px;
}

.conference-comments-list {
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    gap: 40px;
}
</style>
